// src/pages/healthcare/settings/CustomTestPackages.tsx

import React, { useState, useEffect, useCallback } from "react";
import { Button, Space, Modal, Form, Input, Select, Switch, Popconfirm, Alert, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, LockOutlined } from "@ant-design/icons";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { useNotifications } from "../../../context/NotificationContext";
import { DataTable, SectionCard, StatusTag } from "../../../components/shared";
import { fetchCustomTestPackages, createCustomTestPackage, updateCustomTestPackage, deleteCustomTestPackage, toggleCustomTestPackageActive } from "../../../api/healthcare/Testing/CustomTestPackageApi";
import { fetchTestCategories } from "../../../api/healthcare/Testing/TestCategories";
import { fetchTestTypesByCategory } from "../../../api/healthcare/Testing/TestTypes";
import { CustomTestPackage, TestCategory, TestType } from "../../../types/healthcare/TestTypes";
import { Company } from "../../../types/admin/CompanyTypes";
import { User } from "../../../types/admin/UserTypes";
import { fetchClinicCompany } from "../../../api/admin/companyApi";

const CustomTestPackages: React.FC = () => {
	const [packages, setPackages] = useState<CustomTestPackage[]>([]);
	const [testCategories, setTestCategories] = useState<TestCategory[]>([]);
	const [testTypes, setTestTypes] = useState<TestType[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [editingPackage, setEditingPackage] = useState<CustomTestPackage | null>(null);
	const [userCompany, setUserCompany] = useState<Company | null>(null);

	const [form] = Form.useForm();
	const { showError, showSuccess } = useNotifications();
	const user: User = getSessionUser();

	const loadUserCompany = useCallback(async () => {
		try {
			const comp = await fetchClinicCompany();
			setUserCompany(comp);
		} catch (error) {
			showError("Failed to load company information");
		}
	}, [showError]);

	const loadTestCategories = useCallback(async () => {
		if (!userCompany?.id) return;

		try {
			const response = await fetchTestCategories({
				include_test_types: true,
			});
			if (response.success) {
				setTestCategories(response.data.filter((cat) => cat.is_active));
			}
		} catch (error) {
			showError("Failed to load test categories");
		}
	}, [userCompany?.id, showError]);

	const loadPackages = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchCustomTestPackages({
				active_only: false,
				include_analysis_types: true,
			});
			setPackages(data);
		} catch (error) {
			showError("Failed to load custom test packages");
		} finally {
			setLoading(false);
		}
	}, [showError]);

	useEffect(() => {
		loadUserCompany();
		loadPackages();
	}, []);

	useEffect(() => {
		if (userCompany?.id) {
			loadTestCategories();
		}
	}, [userCompany?.id, loadTestCategories]);

	const handleCategoryChange = async (categoryId: number) => {
		const category = testCategories.find((c) => c.id === categoryId);
		if (!category || !userCompany?.id) return;

		try {
			const response = await fetchTestTypesByCategory(category.value, userCompany.id);
			if (response.success) {
				setTestTypes(response.data.filter((type) => type.is_active));
			}
		} catch (error) {
			showError("Failed to load analysis types");
		}
	};

	const handleCreate = () => {
		setEditingPackage(null);
		setTestTypes([]);
		form.resetFields();
		setModalVisible(true);
	};

	const handleEdit = async (pkg: CustomTestPackage) => {
		setEditingPackage(pkg);

		if (pkg.test_category_id) {
			await handleCategoryChange(pkg.test_category_id);
		}

		form.setFieldsValue({
			name: pkg.name,
			value: pkg.value,
			description: pkg.description,
			category_id: pkg.test_category_id,
			analysis_types: pkg.analysis_types,
			is_active: pkg.is_active,
			creates_lab_record: pkg.creates_lab_record,
			sort_order: pkg.sort_order,
		});
		setModalVisible(true);
	};

	const handleDelete = async (id: number) => {
		try {
			await deleteCustomTestPackage(id);
			showSuccess("Custom test package deleted successfully");
			loadPackages();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to delete custom test package");
		}
	};

	const handleToggleActive = async (pkg: CustomTestPackage) => {
		try {
			await toggleCustomTestPackageActive(pkg.id);
			showSuccess("Custom test package status updated successfully");
			loadPackages();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update custom test package status");
		}
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();

			if (editingPackage) {
				await updateCustomTestPackage(editingPackage.id, values);
				showSuccess("Custom test package updated successfully");
			} else {
				await createCustomTestPackage(values);
				showSuccess("Custom test package created successfully");
			}

			setModalVisible(false);
			form.resetFields();
			loadPackages();
		} catch (error: any) {
			if (error.errorFields) {
				return;
			}
			showError(error.response?.data?.message || "Failed to save custom test package");
		}
	};

	const columns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
			width: 200,
			render: (text: string, record: CustomTestPackage) => (
				<div>
					<div style={{ fontWeight: 600 }}>
						{record.is_global && (
							<Tooltip title="Managed by global admin system">
								<LockOutlined style={{ marginRight: 6, color: '#722ed1' }} />
							</Tooltip>
						)}
						{text}
					</div>
					<div style={{ fontSize: 12, opacity: 0.65 }}>{record.value}</div>
				</div>
			),
		},
		{
			title: "Analysis Types",
			dataIndex: "analysis_types",
			key: "analysis_types",
			render: (_: any, record: CustomTestPackage) => (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
					{record.analysis_type_details?.map((type) => (
						<span key={type.value} style={{ background: "var(--mg-info-bg)", color: "var(--mg-info)", border: "1px solid var(--mg-info-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>
							{type.name}
						</span>
					))}
				</div>
			),
		},
		{
			title: "Category",
			dataIndex: "test_category",
			key: "test_category",
			width: 150,
			render: (category: TestCategory | undefined) => (
				category
					? <span style={{ background: "var(--mg-primary-bg)", color: "var(--mg-primary)", border: "1px solid var(--mg-primary-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>{category.name}</span>
					: <span style={{ opacity: 0.5 }}>Mixed</span>
			),
		},
		{
			title: "Providers",
			dataIndex: "available_providers_count",
			key: "available_providers_count",
			width: 100,
			align: "center" as const,
			render: (count: number | undefined) => (
				<span style={count && count > 0 ? { color: "var(--mg-success)" } : { color: "var(--mg-warning)" }}>{count ?? 0}</span>
			),
		},
		{
			title: "Requirements",
			key: "requirements",
			width: 150,
			render: (_: any, record: CustomTestPackage) => (
				<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
					{record.requires_fasting && <span style={{ background: "var(--mg-warning-bg)", color: "var(--mg-warning)", border: "1px solid var(--mg-warning-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Fasting</span>}
					{record.requires_contrast && <span style={{ background: "var(--mg-danger-bg)", color: "var(--mg-danger)", border: "1px solid var(--mg-danger-border)", borderRadius: 4, padding: "1px 6px", fontSize: 12 }}>Contrast</span>}
				</div>
			),
		},
		{
			title: "Status",
			dataIndex: "is_active",
			key: "is_active",
			width: 100,
			render: (isActive: boolean) => <StatusTag status={isActive ? "active" : "inactive"} />,
		},
		{
			title: "Auto Lab Record",
			dataIndex: "creates_lab_record",
			key: "creates_lab_record",
			width: 130,
			align: "center" as const,
			render: (creates: boolean) => (
				<span style={creates ? { color: "var(--mg-success)", fontWeight: 600 } : { color: "var(--mg-warning)" }}>
					{creates ? "Yes" : "No"}
				</span>
			),
		},
		{
			title: "",
			key: "actions",
			width: 110,
			render: (_: any, record: CustomTestPackage) => (
				<Space size={2}>
					<Tooltip title={record.is_global ? "Managed by admin system" : "Edit"}>
						<Button type="text" size="small" icon={<EditOutlined />} disabled={record.is_global} onClick={(e) => { e.stopPropagation(); handleEdit(record); }} />
					</Tooltip>
					<Tooltip title={record.is_global ? "Managed by admin system" : (record.is_active ? "Deactivate" : "Activate")}>
						<Button type="text" size="small" icon={<PoweroffOutlined />} disabled={record.is_global} onClick={(e) => { e.stopPropagation(); handleToggleActive(record); }} />
					</Tooltip>
					<Popconfirm title="Are you sure you want to delete this package?" disabled={record.is_global} onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
						<Button type="text" danger size="small" disabled={record.is_global} icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<SectionCard
			title="Custom Test Packages"
			extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Create Package</Button>}
		>
			<Alert message="Custom Test Packages" description="Create custom test packages that combine multiple analysis types. These packages will be available when ordering tests." type="info" showIcon style={{ marginBottom: 16 }} />

			<DataTable
				columns={columns}
				dataSource={packages}
				rowKey="id"
				loading={loading}
				pagination={{ pageSize: 10 }}
				emptyTitle="No custom test packages found"
				emptyDescription="Create a custom test package to combine multiple analysis types"
				emptyAction={{ label: "Create Package", onClick: handleCreate }}
			/>

			<Modal
				title={editingPackage ? "Edit Custom Test Package" : "Create Custom Test Package"}
				open={modalVisible}
				onOk={handleSubmit}
				onCancel={() => {
					setModalVisible(false);
					form.resetFields();
				}}
				width={700}
				okText={editingPackage ? "Update" : "Create"}
			>
				<Form form={form} layout="vertical">
					<Form.Item name="name" label="Package Name" rules={[{ required: true, message: "Please enter package name" }]}>
						<Input placeholder="e.g., Healthspan Package One" />
					</Form.Item>

					<Form.Item name="value" label="Package Code (Optional)" help="Leave empty to auto-generate from name">
						<Input placeholder="e.g., healthspan-package-one" />
					</Form.Item>

					<Form.Item name="description" label="Description">
						<Input.TextArea rows={3} placeholder="Describe what this package includes" />
					</Form.Item>

					<Form.Item name="category_id" label="Test Category" help="Select a category to filter available analysis types">
						<Select
							placeholder="Select category"
							allowClear
							onChange={handleCategoryChange}
							options={testCategories.map((cat) => ({
								label: cat.name,
								value: cat.id,
							}))}
						/>
					</Form.Item>

					<Form.Item name="analysis_types" label="Analysis Types" rules={[{ required: true, message: "Please select at least one analysis type" }]}>
						<Select
							mode="multiple"
							placeholder="Select analysis types to include"
							options={testTypes.map((type) => ({
								label: type.name,
								value: type.value,
							}))}
							notFoundContent={testTypes.length === 0 ? "Select a category first" : "No analysis types available"}
						/>
					</Form.Item>

					<Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
						<Switch />
					</Form.Item>

					<Form.Item
						name="creates_lab_record"
						label="Auto-create Lab Record"
						valuePropName="checked"
						initialValue={true}
						help="When enabled and no external provider is selected, ordering this package will automatically create a record in your company's LabIQ system."
					>
						<Switch />
					</Form.Item>

					<Form.Item name="sort_order" label="Sort Order" initialValue={0}>
						<Input type="number" min={0} />
					</Form.Item>
				</Form>
			</Modal>
		</SectionCard>
	);
};

export default CustomTestPackages;
